import { ColumnType } from '@ingestro/importer-react'
import { ValidatorAPI } from '@ingestro/importer-react'
import { NuvoImporterQuery } from 'generated/resources/graphql'

// Convert backend columnValues array to Nuvo ColumnValueAPI Record format
function convertColumnValuesToRecord(columnValues?: Array<{ column: string; values: string[] }>) {
  return (
    columnValues?.reduce<Record<string, string[]>>((acc, columnValue) => {
      if (columnValue.column && columnValue.values) {
        acc[columnValue.column] = columnValue.values
      }
      return acc
    }, {}) || undefined
  )
}

interface UseColumnCustomizationsProps {
  data?: NuvoImporterQuery
}

export type ColumnCustomization = {
  key: string
  label: string
  columnType: ColumnType
  hidden?: boolean
  disabled?: boolean
  validations?: ValidatorAPI[] | null
  customizations?: {
    type: 'transformation' | 'validation'
    computeWith: string
    parameters?:
      | {
          key: string
          value: string
        }[]
      | null
  }[]
}

export const useColumnCustomizations = ({ data }: UseColumnCustomizationsProps) => {
  // Collect all schema column keys to filter cross-column validation references
  // (e.g. identifier_type may be excluded by country capability filters)
  const allColumnKeys = new Set(
    data?.universalImporter.schemaAdapters.map((node) => node.key) || []
  )

  const columns: ColumnCustomization[] =
    data?.universalImporter.schemaAdapters
      .filter(({ customizations }) => customizations && customizations.length > 0)
      .map(({ key, label, columnType, hidden, disabled, validations, customizations }) => ({
        key,
        label,
        columnType,
        hidden,
        disabled,
        validations:
          validations?.reduce<ValidatorAPI[]>((acc, { __typename: _, ...validation }) => {
            const filteredColumns = validation.columns?.filter((col) => allColumnKeys.has(col))

            // Skip required_with/required_with_all validations when all dependent columns
            // have been excluded from the schema
            if (
              (validation.validate === 'required_with' ||
                validation.validate === 'required_with_all') &&
              filteredColumns &&
              filteredColumns.length === 0
            ) {
              return acc
            }

            acc.push({
              ...validation,
              errorMessage: validation.errorMessage || '',
              columns: filteredColumns?.length ? filteredColumns : undefined,
              columnValues: convertColumnValuesToRecord(validation.columnValues || undefined),
            })
            return acc
          }, []) || null,
        customizations: customizations
          ?.map(({ __typename: _, ...customization }) => ({
            ...customization,
          }))
          .sort((a, b) => a.type.localeCompare(b.type)), // Sort by type (transformations first)
      })) || []

  // Sort columns by hidden (true first)
  columns.sort((a, b) => Number(b.hidden) - Number(a.hidden))

  return { columns }
}
